import { Fragment, useEffect, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { messaging } from '@cwos/api-client';
import { relativeTime } from '@cwos/ui-shared';
import { ConsequenceNote, DualTimestamp, Pill, SkeletonSet, SuccessEmpty } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { usePermissions } from '../permissions.js';
import { authorityGate, gateOpen } from '../authorityGate.js';
import { GateNotice } from '../GateNotice.js';
import {
  automatedLabel,
  channelTitle,
  errorMessage,
  foldThreads,
  isUnreachable,
  kindMeta,
  mentionSegments,
  messagingKeys,
  planMessageRows,
  replyCountLabel,
  useThread,
  useMeUserId,
  senderInitials,
  senderLabel,
  useChannel,
  useMessagingPolicy,
  useMessages,
  useSendMessage,
  useSetMuted,
  useToggleReaction,
} from './messaging.js';
import { MemberManager } from './MemberManager.js';
import { MessageSearch } from './MessageSearch.js';
import { COMMON_EMOJI, EmojiPicker } from './EmojiPicker.js';
import { GifPicker } from './GifPicker.js';
import { MentionPicker } from './MentionPicker.js';
import { LoadFailure } from '../LoadFailure.js';

type MessageView = messaging.MessageView;

/**
 * A single conversation (doc 01 comms). Messages render with sender identity
 * (never a UUID) and DualTimestamp; the composer carries a consequence note and
 * opening the thread marks it read. Members management sits alongside, shown for
 * managers only. A refusal — 403 or 404 — renders an honest "not open to you"
 * panel rather than a retry box for something that will never load.
 */
export function ChannelThread() {
  const navigate = useNav();
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { channelId?: string };
  const id = params.channelId ?? '';

  const channel = useChannel(id);
  const messages = useMessages(id);

  // Mark read only AFTER the messages actually loaded — never on open alone.
  // Marking on mount cleared the unread state even when the fetch failed, which
  // is claiming the reader saw messages they never received. Keyed on the
  // newest message id so messages that arrive while the thread is open (the
  // 30s poll) are marked too, without re-PUTting on every poll that brings
  // nothing new.
  const latest = messages.data?.messages.at(-1)?.id ?? null;
  const marked = useRef<string | null>(null);
  useEffect(() => {
    if (!id || !messages.isSuccess) return;
    const key = `${id}:${latest ?? 'empty'}`;
    if (marked.current === key) return;
    marked.current = key;
    let cancelled = false;
    void messaging
      .markRead(id)
      .then(() => {
        if (cancelled) return;
        void qc.invalidateQueries({ queryKey: messagingKeys.channels });
        // Marking the room read also read its mentions of you (one act,
        // server-side) — the badge must stop claiming otherwise.
        void qc.invalidateQueries({ queryKey: messagingKeys.mentions });
      })
      .catch(() => {
        /* read-state is best-effort; never blocks the thread */
      });
    return () => {
      cancelled = true;
    };
  }, [id, qc, messages.isSuccess, latest]);

  // 403 OR 404. The API answers 404 for a channel you are not in (withholding
  // contract §H — a 403 confirms the row exists), so branching on 403 alone
  // would send a manager who opened a stale link to the generic "we couldn't
  // load this, retry" panel for something that will never load. `isUnreachable`
  // keeps both in the honest branch.
  const notMember =
    (channel.isError && isUnreachable(channel.error)) ||
    (messages.isError && isUnreachable(messages.error));

  return (
    <div className="rn-col" style={{ maxWidth: 900 }}>
      <div>
        <button type="button" className="rn-link" onClick={() => navigate('/messages')}>
          ← Messages
        </button>
      </div>

      {notMember ? (
        <section className="rn-card">
          <h2 className="rn-card-title">This conversation isn’t open to you</h2>
          <p className="rn-muted" style={{ marginBottom: 0 }}>
            It is either no longer there, or you are not a member of it — the server does not say
            which, on purpose, because saying so would confirm that a conversation exists to
            somebody who cannot read it. If you were expecting to see it, ask an owner to add you.
          </p>
        </section>
      ) : channel.isLoading ? (
        <SkeletonSet surface="drawer" />
      ) : channel.isError && !channel.data ? (
        <LoadFailure
          error={channel.error}
          mainRead
          message="We couldn’t load this conversation just now."
          onRetry={() => void channel.refetch()}
        />
      ) : channel.data ? (
        <Loaded channelId={id} detail={channel.data} messages={messages} />
      ) : null}
    </div>
  );
}

function Loaded({
  channelId,
  detail,
  messages,
}: {
  channelId: string;
  detail: messaging.ChannelWithMembers;
  messages: ReturnType<typeof useMessages>;
}) {
  const meta = kindMeta(detail.channel.kind);
  // Gated on the SAME permissions the server checks (routes.ts): channel
  // create/update and member add/remove need `messaging:manage`; sending,
  // reacting, replying, muting and marking read need `messaging:send`. Neither
  // is a per-channel "owner" role — the server does not check one for these
  // routes, so an affordance gated on local ownership could either hide a
  // control someone can actually use, or offer one that 403s.
  const permissions = usePermissions();
  const canManage = permissions.has('messaging:manage');
  /**
   * FOUR STATES, BECAUSE THIS ROUTE IS UNGATED.
   *
   * `/messages/$channelId` is registered with no `gated(...)` wrapper
   * (App.tsx), deliberately — messaging needs no read key. So nothing upstream
   * has already answered the unresolved and unreadable cases the way
   * `RequirePermission` answers them for every gated screen, and this subtree
   * mounts as soon as the CHANNEL query resolves, independently of
   * `/auth/me`.
   *
   * `has('messaging:send')` is false in all three of "not arrived yet",
   * "could not be read" and "you do not hold it", and this screen used to
   * render the third sentence — "You have read-only access to this
   * conversation" — off any of them. A holder deep-linking into a
   * conversation from a notification saw their composer replaced by a flat
   * accusation while the permission set was still in flight; if `/auth/me`
   * had failed, it was PERMANENT and carried no retry, because a refusal does
   * not offer one.
   *
   * The refusal copy is unchanged and still this screen's own (`refusedNode`).
   * What is added is the two answers that were missing.
   */
  const sendGate = authorityGate(permissions, 'messaging:send');
  const canSend = gateOpen(sendGate);
  const mute = useSetMuted(channelId);
  const policy = useMessagingPolicy();
  // The message being quoted, lifted here so a Reply button in the list and the
  // composer's quote banner share one piece of state.
  const [replyTo, setReplyTo] = useState<MessageView | null>(null);
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const archived = detail.channel.status === 'archived';
  const showMessage = (messageId: string) => {
    const row = document.getElementById(`msg-${messageId}`);
    if (!row) {
      setSearchNotice(
        'That message is earlier than what is loaded here. Use “Show earlier messages” and search again.',
      );
      return;
    }
    setSearchNotice(null);
    row.scrollIntoView({ block: 'center' });
    row.classList.add('msg-flash');
    if (flashTimer.current !== null) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => {
      row.classList.remove('msg-flash');
      flashTimer.current = null;
    }, 1500);
  };

  return (
    <div className="msg-thread-grid">
      <div className="rn-col">
        <section className="rn-card">
          <div className="rn-card-head">
            <h2 className="rn-card-title">{channelTitle(detail.channel)}</h2>
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-8)' }}>
              <Pill variant={meta.variant}>{meta.label}</Pill>
              {canSend ? (
                <button
                  type="button"
                  className="rn-link"
                  disabled={mute.isPending}
                  onClick={() => mute.mutate(!detail.channel.muted)}
                >
                  {detail.channel.muted ? 'Unmute' : 'Mute'}
                </button>
              ) : null}
            </span>
          </div>
          {mute.isError ? (
            <p className="msg-error" style={{ margin: 0 }}>
              {errorMessage(mute.error, 'We couldn’t change that just now.')} Try again.
            </p>
          ) : null}
        </section>

        <section className="rn-card">
          <MessageList
            channelId={channelId}
            messages={messages}
            reactionsEnabled={policy.data?.reactionsEnabled ?? true}
            canSend={canSend}
            onReply={setReplyTo}
          />
        </section>

        {archived ? (
          <section className="rn-card">
            <p className="rn-muted" style={{ margin: 0 }}>
              This conversation is archived. Its history stays readable, but no new messages can be
              sent.
            </p>
          </section>
        ) : canSend ? (
          <Composer
            channelId={channelId}
            gifsEnabled={policy.data?.gifsEnabled ?? true}
            members={detail.members}
            replyTo={replyTo}
            onClearReply={() => setReplyTo(null)}
          />
        ) : (
          <GateNotice
            gate={sendGate}
            what="send messages in this conversation"
            onRetry={() => void permissions.refetch()}
            refusedNode={
              <section className="rn-card">
                <p className="rn-muted" style={{ margin: 0 }}>
                  You have read-only access to this conversation.
                </p>
              </section>
            }
          />
        )}
      </div>

      <aside className="rn-col">
        <section className="rn-card">
          <h2 className="rn-card-title">Search</h2>
          <MessageSearch channelId={channelId} onShow={showMessage} />
          {searchNotice ? (
            <p className="msg-error" role="status">
              {searchNotice}
            </p>
          ) : null}
        </section>
        <MemberManager channelId={channelId} members={detail.members} canManage={!!canManage} />
      </aside>
    </div>
  );
}

/** The page size this screen reads with — shared by the first page and "earlier". */
const MESSAGE_PAGE = 100;

function MessageList({
  channelId,
  messages,
  reactionsEnabled,
  canSend,
  onReply,
}: {
  channelId: string;
  messages: ReturnType<typeof useMessages>;
  reactionsEnabled: boolean;
  canSend: boolean;
  onReply: (m: MessageView) => void;
}) {
  // Older pages, fetched on demand via the server's opaque (createdAt, id)
  // cursor (F193) and kept outside react-query's polling window (the poll
  // refreshes only the newest page; history does not change). Without this,
  // message 101 simply did not exist as far as the screen was concerned.
  // Once we have paged, the LAST older page's cursor/hasMore is the boundary
  // truth; before that, the newest page's own envelope is.
  const [older, setOlder] = useState<MessageView[]>([]);
  const [olderPage, setOlderPage] = useState<{
    nextCursor: string | null;
    hasMore: boolean;
  } | null>(null);
  const [olderLoading, setOlderLoading] = useState(false);
  const [olderError, setOlderError] = useState(false);

  if (messages.isLoading) return <SkeletonSet surface="table" rows={5} />;
  if (messages.isError && !messages.data) {
    return (
      <LoadFailure
        error={messages.error}
        message="We couldn’t load these messages just now."
        onRetry={() => void messages.refetch()}
      />
    );
  }
  const latest = messages.data?.messages ?? [];
  // Oldest-first merge, deduped by id (a message can sit in an older page and
  // scroll into the newest window's 100 after deletions never happen — but a
  // boundary overlap must not render twice).
  const seen = new Set<string>();
  const all = [...older, ...latest].filter((m) =>
    seen.has(m.id) ? false : (seen.add(m.id), true),
  );
  if (all.length === 0) {
    return (
      <SuccessEmpty
        heading="No messages yet"
        message="Say hello — the first message starts the thread."
      />
    );
  }

  // The server says whether history continues — no full-page guessing.
  const mayHaveEarlier = olderPage ? olderPage.hasMore : (messages.data?.hasMore ?? false);
  const earlierCursor = olderPage ? olderPage.nextCursor : (messages.data?.nextCursor ?? null);

  const loadEarlier = () => {
    if (earlierCursor === null) return;
    setOlderLoading(true);
    setOlderError(false);
    messaging
      .listMessagesPage(channelId, { cursor: earlierCursor, limit: MESSAGE_PAGE })
      .then((page) => {
        setOlder((prev) => [...page.messages, ...prev]);
        setOlderPage({ nextCursor: page.nextCursor, hasMore: page.hasMore });
      })
      .catch(() => setOlderError(true))
      .finally(() => setOlderLoading(false));
  };

  // Replies collapse under the root the SERVER put them in, and only then —
  // `foldThreads` reads `threadRootId` and nothing else. Day separators and
  // same-sender grouping are then planned over the folded flow, so a thread's
  // replies never split a run or manufacture a separator inside one.
  const { rows: flow, repliesByRoot } = foldThreads(all);
  const plan = planMessageRows(flow);

  return (
    <>
      {mayHaveEarlier ? (
        <div className="msg-earlier">
          <button type="button" className="rn-link" disabled={olderLoading} onClick={loadEarlier}>
            {olderLoading ? 'Loading earlier messages…' : 'Show earlier messages'}
          </button>
          {olderError ? (
            <span className="msg-error">We couldn’t load earlier messages just now.</span>
          ) : null}
        </div>
      ) : null}
      <ul className="msg-messages" aria-label="Messages">
        {plan.map(({ message: m, daySeparator, grouped }) => (
          <Fragment key={m.id}>
            {daySeparator ? (
              <li className="msg-day-sep" aria-label={daySeparator}>
                <span>{daySeparator}</span>
              </li>
            ) : null}
            <MessageRow
              channelId={channelId}
              message={m}
              grouped={grouped}
              reactionsEnabled={reactionsEnabled}
              canSend={canSend}
              onReply={onReply}
            />
            {m.thread ? (
              <ThreadReplies
                channelId={channelId}
                root={m}
                thread={m.thread}
                onPage={repliesByRoot.get(m.id) ?? []}
                reactionsEnabled={reactionsEnabled}
                canSend={canSend}
                onReply={onReply}
              />
            ) : null}
          </Fragment>
        ))}
      </ul>
    </>
  );
}

/**
 * A collapsed thread: "N replies", expanding to the replies themselves.
 *
 * THE COUNT IS THE SERVER'S. `thread.replyCount` is what the database counted
 * across the whole thread, so the label is right even when the page carries
 * only some of the replies (or, on the first paint after a send, none of them).
 * Counting what happened to be in hand would quietly under-report a busy thread.
 *
 * Expanding reads the thread route, and the EXPANDED view renders that
 * response — not the page fragment — so what a reader sees under a root is
 * exactly the thread the server stated, in the server's order, complete. The
 * page-local replies are the fallback while that request is in flight and if it
 * fails: they are real messages the server already sent, so showing them is
 * honest, and the failure is said out loud rather than left as a silent
 * shortfall against a count the reader can see.
 */
function ThreadReplies({
  channelId,
  root,
  thread,
  onPage,
  reactionsEnabled,
  canSend,
  onReply,
}: {
  channelId: string;
  root: MessageView;
  thread: NonNullable<MessageView['thread']>;
  /** The replies this page already carries — the pre-fetch fallback. */
  onPage: MessageView[];
  reactionsEnabled: boolean;
  canSend: boolean;
  onReply: (m: MessageView) => void;
}) {
  const [open, setOpen] = useState(false);
  const query = useThread(channelId, root.id, open);

  const label = replyCountLabel(thread.replyCount);
  const last = relativeTime(new Date(thread.lastReplyAt));

  if (!open) {
    return (
      <li className="msg-thread" data-open="false">
        <button
          type="button"
          className="rn-link msg-thread-toggle"
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          {label}
        </button>
        <time className="rn-muted" dateTime={last.absolute}>
          Last reply {last.short}
        </time>
      </li>
    );
  }

  const replies = query.data?.replies ?? onPage;

  return (
    <li className="msg-thread" data-open="true">
      <button
        type="button"
        className="rn-link msg-thread-toggle"
        aria-expanded={true}
        onClick={() => setOpen(false)}
      >
        Hide {label}
      </button>
      {query.isPending ? <span className="rn-muted">Loading replies…</span> : null}
      {query.isError ? (
        <span className="msg-error">
          We couldn’t load the rest of this thread just now.{' '}
          <button type="button" className="rn-link" onClick={() => void query.refetch()}>
            Try again
          </button>
        </span>
      ) : null}
      <ul className="msg-thread-replies" aria-label={`Replies to ${senderLabel(root)}`}>
        {replies.map((reply) => (
          <MessageRow
            key={reply.id}
            channelId={channelId}
            message={reply}
            grouped={false}
            reactionsEnabled={reactionsEnabled}
            canSend={canSend}
            onReply={onReply}
          />
        ))}
      </ul>
      {query.data?.hasMore ? (
        // The route pages at 200 and this screen asks for 200, so reaching this
        // means a thread longer than any this surface renders. Say so rather
        // than showing part of it as if it were all of it.
        <span className="rn-muted">
          Showing the first {replies.length} of {thread.replyCount} replies.
        </span>
      ) : null}
    </li>
  );
}

function MessageRow({
  channelId,
  message,
  grouped,
  reactionsEnabled,
  canSend,
  onReply,
}: {
  channelId: string;
  message: MessageView;
  /** Continuation of the previous sender's run — the identity header collapses. */
  grouped: boolean;
  reactionsEnabled: boolean;
  canSend: boolean;
  onReply: (m: MessageView) => void;
}) {
  const navigate = useNav();
  const toggle = useToggleReaction(channelId);
  // SENDER-SIDE BUBBLES (Phase F): the reader's own messages sit on the
  // right, everyone else's on the left — the conversation grammar every
  // messaging product teaches. "Mine" is `senderUserId === userId`, both
  // stated by the server; `usePermissions().userId` is the product's one
  // is-this-row-mine identity and is null while unresolved — an unresolved
  // identity is NOT mine, so the left side is the truthful default, never a
  // guess.
  const { userId } = usePermissions();
  const mine = userId !== null && message.senderUserId !== null && message.senderUserId === userId;
  const [reacting, setReacting] = useState(false);

  if (message.kind === 'system') {
    // ATTRIBUTION (§24): a machine line must not dress as a person. The pill
    // names the author kind, and `alertId` — the provenance the data carries —
    // becomes a way into the operational surface the message is about.
    const when = relativeTime(new Date(message.createdAt));
    return (
      <li id={`msg-${message.id}`} className="msg-system" data-kind="system">
        <Pill variant="unsure">System</Pill>
        <span>{message.body}</span>
        <time className="rn-muted" dateTime={when.absolute}>
          {when.short}
        </time>
        {message.alertId ? (
          <button type="button" className="rn-link" onClick={() => navigate('/attention')}>
            Open Needs attention
          </button>
        ) : null}
      </li>
    );
  }

  if (message.kind === 'automated') {
    // ATTRIBUTION, extended for contract #7 (AUTOMATION-CAPABILITY-MODEL rule
    // 3): an automated message renders under its STRUCTURED attribution —
    // "Automated · set up by {owner}" / "Automated · organization rule" / the
    // assistant label — as its own labeled row, the system-pill treatment. The
    // label comes from `automatedLabel` (the one shared renderer of the closed
    // vocabulary), never from message text and never as a raw id; and like a
    // system row, an automated row never wears a person's avatar and breaks a
    // sender's grouped run (planMessageRows groups `user` rows only).
    const when = relativeTime(new Date(message.createdAt));
    return (
      <li id={`msg-${message.id}`} className="msg-system" data-kind="automated">
        <Pill variant="unsure">{automatedLabel(message)}</Pill>
        <span>{message.body}</span>
        <time className="rn-muted" dateTime={when.absolute}>
          {when.short}
        </time>
      </li>
    );
  }

  const react = (emoji: string, mine: boolean) => {
    setReacting(false);
    toggle.mutate({ messageId: message.id, emoji, mine });
  };

  return (
    <li
      id={`msg-${message.id}`}
      className="msg-bubble"
      data-kind="user"
      data-grouped={grouped}
      data-mine={mine}
    >
      {mine ? null : grouped ? (
        <span className="msg-avatar-spacer" aria-hidden="true" />
      ) : (
        <span className="msg-avatar" aria-hidden="true">
          {senderInitials(message)}
        </span>
      )}
      <div className="msg-bubble-content">
        {grouped ? null : (
          <div className="msg-bubble-head">
            {/* Your own bubble needs no name tag — the side says it. */}
            {mine ? null : <span className="msg-sender">{senderLabel(message)}</span>}
            {mine || !message.senderRole ? null : (
              <span className="rn-muted">{message.senderRole}</span>
            )}
            <span className="msg-bubble-time">
              <DualTimestamp occurredAt={message.createdAt} receivedAt={message.createdAt} />
            </span>
          </div>
        )}

        {message.replyTo ? <QuotedPreview replyTo={message.replyTo} /> : null}

        {message.body ? <MessageBody message={message} /> : null}

        {message.attachment ? <GifAttachmentView attachment={message.attachment} /> : null}

        {(message.reactions?.length ?? 0) > 0 ? (
          <div className="msg-reactions">
            {message.reactions!.map((r) => (
              <button
                key={r.emoji}
                type="button"
                className="msg-reaction-chip"
                data-mine={r.mine}
                disabled={toggle.isPending || !reactionsEnabled || !canSend}
                aria-label={`${r.emoji} ${r.count}${r.mine ? ', including you' : ''}. ${
                  r.mine ? 'Remove your reaction' : 'Add your reaction'
                }`}
                title={r.reactors.join(', ')}
                onClick={() => react(r.emoji, r.mine)}
              >
                <span aria-hidden="true">{r.emoji}</span>
                <span className="msg-reaction-count">{r.count}</span>
              </button>
            ))}
          </div>
        ) : null}

        {toggle.isError ? (
          <p className="msg-error" style={{ margin: 0 }}>
            {errorMessage(toggle.error, 'We couldn’t update that reaction just now.')}
          </p>
        ) : null}

        {canSend ? (
          <div className="msg-bubble-actions">
            <button type="button" className="rn-link" onClick={() => onReply(message)}>
              Reply
            </button>
            {reactionsEnabled ? (
              <span className="msg-react-wrap">
                <button
                  type="button"
                  className="rn-link"
                  aria-haspopup="dialog"
                  aria-expanded={reacting}
                  onClick={() => setReacting((v) => !v)}
                >
                  React
                </button>
                {reacting ? (
                  <EmojiPicker
                    label="React with an emoji"
                    onPick={(emoji) => {
                      const existing = message.reactions?.find((r) => r.emoji === emoji);
                      react(emoji, existing?.mine ?? false);
                    }}
                    onClose={() => setReacting(false)}
                  />
                ) : null}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

/**
 * The message body, with mention MARKS drawn from server-validated spans only
 * (F194 seam 3). `mentionSegments` trusts nothing the text claims: a "@name"
 * the server did not validate is plain text, and a span that does not fit the
 * body is dropped. The mark carries the live display name as its title so a
 * renamed person still reads correctly on hover.
 */
function MessageBody({ message }: { message: MessageView }) {
  const segments = mentionSegments(message.body, message.mentions);
  return (
    <div className="msg-body">
      {segments.map((segment, i) =>
        segment.mention ? (
          <mark
            key={i}
            className="msg-mention"
            title={segment.mention.displayName ?? undefined}
            data-user-id={segment.mention.userId}
          >
            {segment.text}
          </mark>
        ) : (
          <Fragment key={i}>{segment.text}</Fragment>
        ),
      )}
    </div>
  );
}

/** One-level quoted preview above a reply. Never a tree; never resurrected content. */
function QuotedPreview({ replyTo }: { replyTo: NonNullable<MessageView['replyTo']> }) {
  return (
    <div className="msg-quote" aria-label="In reply to">
      <span className="msg-quote-sender">{replyTo.senderName ?? 'Someone'}</span>
      <span className="msg-quote-preview">
        {replyTo.unavailable ? 'Original message unavailable' : replyTo.preview}
      </span>
    </div>
  );
}

function GifAttachmentView({ attachment }: { attachment: NonNullable<MessageView['attachment']> }) {
  return (
    <img
      className="msg-gif"
      src={attachment.mediaUrl}
      alt={attachment.altText || 'GIF'}
      width={attachment.width}
      height={attachment.height}
      loading="lazy"
    />
  );
}

function Composer({
  channelId,
  gifsEnabled,
  members,
  replyTo,
  onClearReply,
}: {
  channelId: string;
  gifsEnabled: boolean;
  members: messaging.ChannelMemberView[];
  replyTo: MessageView | null;
  onClearReply: () => void;
}) {
  const meUserId = useMeUserId();
  const [body, setBody] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const [showMention, setShowMention] = useState(false);
  // Proposed mentions (F194 seam 3): what the picker chose, sent as user ids
  // alongside the text. The chips below make every proposal visible and
  // removable — the server validates membership again on send, and a refusal
  // surfaces in the send error like any other.
  const [mentions, setMentions] = useState<Array<{ userId: string; name: string }>>([]);
  const send = useSendMessage(channelId);
  const canSend = body.trim() !== '' && !send.isPending;

  // The roster minus yourself — mentioning yourself is not an affordance
  // worth offering, though the server would not refuse it.
  const mentionable = members.filter((m) => m.userId !== meUserId);

  const afterSend = () => {
    setBody('');
    setShowEmoji(false);
    setShowGif(false);
    setShowMention(false);
    setMentions([]);
    onClearReply();
  };

  const submit = () => {
    if (!canSend) return;
    send.mutate(
      {
        body: body.trim(),
        replyToMessageId: replyTo?.id,
        ...(mentions.length > 0 ? { mentions: mentions.map((m) => ({ userId: m.userId })) } : {}),
      },
      { onSuccess: afterSend },
    );
  };

  const sendGif = (attachment: messaging.GifAttachment) => {
    send.mutate({ body: '', attachment, replyToMessageId: replyTo?.id }, { onSuccess: afterSend });
  };

  const pickMention = (member: messaging.ChannelMemberView) => {
    setShowMention(false);
    setMentions((prev) =>
      prev.some((m) => m.userId === member.userId)
        ? prev
        : [...prev, { userId: member.userId, name: member.userName }],
    );
    // The "@name" text goes into the body so the message reads naturally; the
    // MENTION itself is the structured proposal above — deleting the text
    // does not silently delete the ping (the chip does that, visibly).
    setBody((b) => `${b}${b === '' || /\s$/.test(b) ? '' : ' '}@${member.userName} `);
  };

  return (
    <section className="rn-card msg-composer">
      {replyTo ? (
        <div className="msg-reply-banner">
          <div className="msg-quote" aria-label="Replying to">
            <span className="msg-quote-sender">Replying to {senderLabel(replyTo)}</span>
            <span className="msg-quote-preview">
              {replyTo.body?.trim() || (replyTo.attachment ? 'GIF' : '')}
            </span>
          </div>
          <button type="button" className="rn-link" onClick={onClearReply}>
            Cancel
          </button>
        </div>
      ) : null}

      <label className="msg-label" htmlFor="msg-composer-input">
        Message
      </label>
      <textarea
        id="msg-composer-input"
        className="msg-textarea"
        value={body}
        rows={3}
        placeholder="Write a message…"
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          // Enter sends; Shift+Enter is a newline; a mid-IME Enter is neither
          // (it commits the composition, and sending there posts half a word).
          if (e.shiftKey || e.nativeEvent.isComposing) return;
          e.preventDefault();
          submit();
        }}
      />

      <div className="msg-tools">
        <span className="msg-tool-wrap">
          <button
            type="button"
            className="rn-link"
            aria-haspopup="dialog"
            aria-expanded={showEmoji}
            onClick={() => {
              setShowEmoji((v) => !v);
              setShowGif(false);
            }}
          >
            🙂 Emoji
          </button>
          {showEmoji ? (
            <EmojiPicker
              label="Insert an emoji"
              onPick={(emoji) => setBody((b) => b + emoji)}
              onClose={() => setShowEmoji(false)}
            />
          ) : null}
        </span>
        {mentionable.length > 0 ? (
          <span className="msg-tool-wrap">
            <button
              type="button"
              className="rn-link"
              aria-haspopup="dialog"
              aria-expanded={showMention}
              onClick={() => {
                setShowMention((v) => !v);
                setShowEmoji(false);
                setShowGif(false);
              }}
            >
              @ Mention
            </button>
            {showMention ? (
              <MentionPicker
                members={mentionable}
                picked={new Set(mentions.map((m) => m.userId))}
                onPick={pickMention}
                onClose={() => setShowMention(false)}
              />
            ) : null}
          </span>
        ) : null}
        {gifsEnabled ? (
          <button
            type="button"
            className="rn-link"
            aria-haspopup="dialog"
            aria-expanded={showGif}
            onClick={() => {
              setShowGif((v) => !v);
              setShowEmoji(false);
              setShowMention(false);
            }}
          >
            GIF
          </button>
        ) : null}
        <span className="msg-quick-react" aria-hidden="true">
          {COMMON_EMOJI.slice(0, 6).map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="msg-emoji-btn"
              tabIndex={-1}
              onClick={() => setBody((b) => b + emoji)}
            >
              {emoji}
            </button>
          ))}
        </span>
      </div>

      {showGif ? <GifPicker onPick={sendGif} onClose={() => setShowGif(false)} /> : null}

      {mentions.length > 0 ? (
        <div className="msg-mention-chips" aria-label="Will mention">
          <span className="rn-muted">Mentions:</span>
          {mentions.map((m) => (
            <span key={m.userId} className="msg-mention-chip">
              @{m.name}
              <button
                type="button"
                className="msg-mention-remove"
                aria-label={`Remove mention of ${m.name}`}
                onClick={() => setMentions((prev) => prev.filter((p) => p.userId !== m.userId))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <ConsequenceNote text="Sending posts this to everyone in the conversation right away." />
      {send.isError ? (
        <p className="msg-error">
          {errorMessage(send.error, 'We couldn’t send that just now.')} Your text is still here —
          try again.
        </p>
      ) : null}
      <div className="msg-composer-actions">
        <span className="rn-muted">Enter to send · Shift+Enter for a new line</span>
        <button type="button" className="msg-primary" disabled={!canSend} onClick={submit}>
          {send.isPending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </section>
  );
}
